import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { FieldEditActions } from '../shared/FieldEditActions'
import { useEditContainerClose } from '../shared/useEditContainerClose'

export type MemberSearchDescriptionEditProps = {
  initiativeId: number
  searchingForMembersDescription: string
}

export const MemberSearchDescriptionEdit = ({
  initiativeId,
  searchingForMembersDescription,
}: MemberSearchDescriptionEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error(
      'MemberSearchDescriptionEdit requires an EditContainer ancestor',
    )
  }
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const t = useTranslations().app.common
  const creationTranslations =
    useTranslations().app.dashboard.initiative.creation

  const [value, setValue] = useState(searchingForMembersDescription)

  useEffect(() => {
    setValue(searchingForMembersDescription)
  }, [searchingForMembersDescription])

  const save = useCallback(() => {
    void client
      .call('edit-initiative', {
        id: initiativeId,
        value: {
          searchingForMembersDescription: value,
        },
      })
      .then((success) => {
        if (success) {
          showSuccessToast(t.toastSuccess)
          close()
        } else {
          showFailToast()
        }
      })
      .catch(() => showFailToast())
  }, [
    client,
    initiativeId,
    value,
    close,
    showFailToast,
    showSuccessToast,
    t.toastSuccess,
  ])

  return (
    <Column gap="1rem" alignItems="stretch">
      <Column gap="0.25rem" alignItems="stretch">
        <Text.Medium>{creationTranslations.lookingForMembersTitle}</Text.Medium>
        <Text.Medium color="var(--palette-blue-500)" isBold={true}>
          {creationTranslations.lookingForMembersSubtitle}
        </Text.Medium>
      </Column>
      <MultiLineTextInput
        placeholder={creationTranslations.lookingForMembersPlaceholder}
        value={value}
        setValue={setValue}
      />
      <FieldEditActions onCancel={close} onSave={save} />
    </Column>
  )
}
